/**
 * simple-slot public API.
 *
 *   import { createSlot } from 'simple-slot';
 *   const slot = createSlot(document.getElementById('slot'), { theme: 'classic', brand: 'lucky-lion', balance: 1000 });
 *   await slot.ready;
 *
 * UMD build exposes the same as window.SimpleSlot.
 */
import type {
  Brand,
  BridgeInbound,
  EngineEventName,
  EngineEvents,
  EngineListener,
  GameSnapshot,
  ResolvedBrand,
  ResolvedTheme,
  RoundState,
  SlotHandle,
  SlotOptions,
  Strings,
  Theme,
  Wallet,
} from './types';
import { AudioManager } from './audio/audio';
import { applyBrand, validateBrand } from './config/brand';
import { loadBrand, loadConfig, loadTheme, pickStrings, resolveBasePath, validateTheme } from './config/loader';
import { Bridge } from './embed/bridge';
import { Game } from './engine/game';
import { DemoWallet } from './engine/wallet';
import { CryptoRng } from './math/rng';
import { createPresenter, type PixiPresenter } from './render/app';
import { Hud, preloadSpinArt } from './ui/hud';
import { injectStyles } from './ui/styles';

export const VERSION = '0.2.0';

export type * from './types';
export { Game } from './engine/game';
export { DemoWallet } from './engine/wallet';
export { CryptoRng, SeededRng } from './math/rng';
export { spin, spinWithStops, forceOutcome, winTier, anticipationReel } from './math/spin';
export { evaluate } from './math/evaluate';
export { analyze, simulate } from './math/analyze';
export { DEFAULT_THEME, DEFAULT_CURRENCY, mergeTheme } from './config/defaults';
export { DEFAULT_BRAND, mergeBrand, applyBrand, validateBrand, brandCssVars } from './config/brand';
export { loadConfig, loadTheme, loadBrand, loadCatalog, validateMath, validateTheme } from './config/loader';
export { defaultOperatorSettings, normalizeSettings, toOverrides, toSlotOptions, encodeSettings, decodeSettings, launchUrl, loadSettings, saveSettings, clearSettings } from './platform/operator';

/** Inject @font-face rules / stylesheets for theme and brand fonts, and wait for them so canvas text uses them. */
async function ensureFonts(theme: ResolvedTheme): Promise<void> {
  if (typeof document === 'undefined') return;
  const specs = [theme.fonts.display, theme.fonts.ui, theme.fonts.mono].filter((f): f is NonNullable<typeof f> => !!f);
  const families = new Set<string>();
  for (const f of specs) {
    const family = f.family.split(',')[0].trim().replace(/^['"]|['"]$/g, '');
    if (f.css) {
      const id = `simple-slot-fontcss-${f.css.replace(/[^a-z0-9]/gi, '_')}`;
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = f.css;
        document.head.appendChild(link);
        await new Promise<void>((r) => {
          link.onload = () => r();
          link.onerror = () => r();
          setTimeout(r, 1500);
        });
      }
      families.add(`${typeof f.weight === 'number' ? f.weight : 400} 16px '${family}'`);
    } else if (f.src) {
      const id = `simple-slot-font-${family}`;
      if (!document.getElementById(id)) {
        const style = document.createElement('style');
        style.id = id;
        style.textContent = `@font-face{font-family:'${family}';src:url('${f.src}');font-weight:${f.weight ?? 'normal'};font-display:swap}`;
        document.head.appendChild(style);
      }
      families.add(`${f.weight ?? 400} 16px '${family}'`);
    }
  }
  await Promise.all(
    [...families].map((spec) =>
      Promise.race([document.fonts.load(spec).catch(() => undefined), new Promise((r) => setTimeout(r, 2000))]),
    ),
  );
}

function stripSnapshot(s: GameSnapshot): RoundState {
  const { state: _s, balance: _b, lastWin: _w, canSpin: _c, canChangeBet: _cb, quickStopAvailable: _q, ...round } = s;
  return round;
}

export function createSlot(container: HTMLElement, options: SlotOptions = {}): SlotHandle {
  let game: Game | null = null;
  let presenter: PixiPresenter | null = null;
  let hud: Hud | null = null;
  let audio: AudioManager | null = null;
  let bridge: Bridge | null = null;
  let wallet: Wallet | null = null;
  let destroyed = false;
  let basePath = './';
  // Current composition: base theme + brand => branded theme; locale + branded theme strings => strings.
  let baseTheme: ResolvedTheme | null = null;
  let brand: ResolvedBrand | null = null;
  let locale: Strings = {};
  let lang = 'en';
  let math: import('./types').MathConfig | null = null;
  const pending: Array<{ name: EngineEventName; fn: EngineListener<EngineEventName> }> = [];
  const unsubs: Array<() => void> = [];
  const cleanup: Array<() => void> = [];
  let visibilityPaused = false;
  let swapping: Promise<void> = Promise.resolve();

  injectStyles(container);

  const ready = (async () => {
    basePath = resolveBasePath(options.basePath);
    const cfg = await loadConfig({ ...options, basePath });
    if (destroyed) return;
    baseTheme = cfg.baseTheme;
    brand = cfg.brand;
    locale = cfg.locale;
    lang = cfg.lang;
    math = cfg.math;
    await ensureFonts(cfg.theme);
    await preloadSpinArt(cfg.theme);
    audio = new AudioManager(cfg.theme);
    wallet = options.wallet ?? new DemoWallet(options.balance ?? 1000, options.storageKey === undefined ? 'simple-slot' : options.storageKey);

    const loading = document.createElement('div');
    loading.className = 'slot-loading';
    loading.style.background = brand?.loadingBackground ?? cfg.theme.palette.background;
    loading.innerHTML = `<div class="slot-loading__title"></div><div class="slot-loading__bar"><div class="slot-loading__fill"></div></div>`;
    (loading.querySelector('.slot-loading__title') as HTMLElement).textContent = cfg.theme.customTitle ?? cfg.theme.name;
    container.appendChild(loading);
    const fill = loading.querySelector('.slot-loading__fill') as HTMLElement;

    presenter = await createPresenter({
      container,
      theme: cfg.theme,
      math: cfg.math,
      audio,
      strings: cfg.strings,
      currency: cfg.currency,
      onProgress: (p) => (fill.style.width = `${Math.round(p * 100)}%`),
    });
    if (destroyed) {
      presenter.destroy();
      return;
    }
    void presenter.setWatermark(brand?.watermark ?? null);
    game = new Game({
      math: cfg.math,
      jurisdiction: cfg.jurisdiction,
      rng: options.rng ?? new CryptoRng(),
      presenter,
      wallet,
      server: options.server,
      autoSpinDelayMs: cfg.theme.animations.timing.freeSpins.betweenSpinsMs,
      restore: options.restore,
      quickSpin: options.restore?.quickSpin,
    });
    for (const p of pending) unsubs.push(game.on(p.name, p.fn));
    pending.length = 0;
    if (options.onEvent) {
      const names: EngineEventName[] = ['state', 'balance', 'bet', 'settings', 'spinStart', 'reelStop', 'spinEnd', 'win', 'freeSpinsStart', 'freeSpinsUpdate', 'freeSpinsEnd', 'error', 'snapshot'];
      for (const n of names) unsubs.push(game.on(n, (payload) => options.onEvent!(n, payload as EngineEvents[typeof n])));
    }
    unsubs.push(game.on('freeSpinsUpdate', (fs) => presenter?.setFreeSpinsCounter(fs.remaining, fs.totalWin)));

    hud = new Hud({
      container,
      game,
      math: cfg.math,
      jurisdiction: cfg.jurisdiction,
      theme: cfg.theme,
      brand,
      strings: cfg.strings,
      currency: cfg.currency,
      audio,
      debug: !!options.debug,
    });
    loading.remove();
    hud.showLoading(null);
    if (options.debug) (container as HTMLElement & { __slotDebug?: unknown }).__slotDebug = { presenter, game, audio, get brand() { return brand; } };

    const unlock = () => audio?.unlock();
    for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) {
      window.addEventListener(ev, unlock, { passive: true });
      cleanup.push(() => window.removeEventListener(ev, unlock));
    }
    const onVisibility = () => {
      if (!game) return;
      if (document.hidden) {
        if (game.snapshot.state !== 'paused') {
          visibilityPaused = true;
          game.pause();
        }
      } else if (visibilityPaused) {
        visibilityPaused = false;
        game.resume();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility));

    const embedded = typeof window !== 'undefined' && window.parent !== window;
    if (options.bridge ?? embedded) {
      const origins = options.bridgeOrigins ?? ['*'];
      if (origins.includes('*')) console.warn('[simple-slot] bridge accepts messages from any origin; set bridgeOrigins in production');
      bridge = new Bridge({
        allowedOrigins: origins,
        onMessage: (m: BridgeInbound) => {
          if (!game) return;
          switch (m.type) {
            case 'host:pause':
              game.pause();
              break;
            case 'host:resume':
              game.resume();
              break;
            case 'host:setBalance':
              game.setBalance(m.balance);
              break;
            case 'host:getState':
              bridge?.send({ type: 'slot:state', state: game.serialize() });
              break;
            case 'host:restore':
              bridge?.send({ type: 'slot:error', code: 'RESTORE_UNSUPPORTED_AT_RUNTIME', message: 'Pass restore in createSlot options' });
              break;
            case 'host:setTheme':
              void handle.setTheme(m.theme);
              break;
            case 'host:setBrand':
              void handle.setBrand(m.brand);
              break;
          }
        },
      });
      unsubs.push(
        game.on('state', ({ state }) => bridge?.send(state === 'idle' ? { type: 'slot:idle' } : { type: 'slot:busy' })),
        game.on('balance', ({ balance }) => bridge?.send({ type: 'slot:balance', balance })),
        game.on('spinEnd', ({ book }) => bridge?.send({ type: 'slot:spin', book })),
        game.on('snapshot', (s) => bridge?.send({ type: 'slot:state', state: stripSnapshot(s) })),
        game.on('error', (e) => bridge?.send({ type: 'slot:error', code: e.code, message: e.message })),
      );
    }

    await game.init();
    bridge?.send({ type: 'slot:ready', version: VERSION });
  })();

  /** Re-compose theme + brand and push it to presenter and HUD (serialised; waits for idle). */
  const recompose = (next: { baseTheme?: ResolvedTheme; brand?: ResolvedBrand | null }): Promise<void> => {
    const run = async () => {
      await ready;
      if (destroyed || !game || !presenter || !hud || !baseTheme || !math) return;
      while (game.snapshot.state !== 'idle') await new Promise((r) => setTimeout(r, 100));
      if (next.baseTheme) baseTheme = next.baseTheme;
      if (next.brand !== undefined) brand = next.brand;
      const problems = [...validateTheme(baseTheme, math), ...(brand ? validateBrand(brand) : [])];
      if (problems.length) throw new Error(`INVALID_CONFIG: ${problems.join('; ')}`);
      const theme = applyBrand(baseTheme, brand);
      const strings = { ...locale, ...pickStrings(theme.strings, lang) };
      await ensureFonts(theme);
      await preloadSpinArt(theme);
      await presenter.setTheme(theme);
      presenter.setStrings(strings);
      void presenter.setWatermark(brand?.watermark ?? null);
      hud.setBrand(brand, theme);
      hud.setStrings(strings);
    };
    swapping = swapping.then(run, run);
    return swapping;
  };

  const handle: SlotHandle = {
    ready,
    press: () => game?.press(),
    spin: () => game?.spin() ?? Promise.resolve(),
    setBet: (total) => game?.setBet(total),
    setBalance: (balance) => game?.setBalance(balance),
    setQuickSpin: (on) => game?.setQuickSpin(on),
    setSound: (on) => audio?.setEnabled(on),
    getSnapshot: () => {
      if (!game) throw new Error('NOT_READY');
      return game.snapshot;
    },
    serialize: () => {
      if (!game) throw new Error('NOT_READY');
      return game.serialize();
    },
    pause: () => game?.pause(),
    resume: () => game?.resume(),
    on: (name, fn) => {
      if (game) return game.on(name, fn);
      const entry = { name, fn: fn as EngineListener<EngineEventName> };
      pending.push(entry);
      return () => {
        const i = pending.indexOf(entry);
        if (i >= 0) pending.splice(i, 1);
        else game?.off(name, fn);
      };
    },
    setTheme: async (themeOrName: string | Theme) => {
      await ready;
      const loaded = await loadTheme(themeOrName, basePath);
      await recompose({ baseTheme: loaded });
    },
    setBrand: async (brandOrName: string | Brand | null) => {
      await ready;
      const loaded = await loadBrand(brandOrName, basePath);
      await recompose({ brand: loaded });
    },
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      for (const u of unsubs) u();
      for (const c of cleanup) c();
      bridge?.destroy();
      hud?.destroy();
      game?.destroy();
      if (!game) presenter?.destroy();
      audio?.destroy();
      container.querySelectorAll('.slot-loading').forEach((n) => n.remove());
    },
  };
  return handle;
}
